import { Component, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { DateService } from '../../../../services/date.service';

@Component({
  selector: 'app-debug-date-control',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatSlideToggleModule,
    MatDatepickerModule,
    MatNativeDateModule
],
  template: `
    <mat-card class="debug-card" [class.debug-active]="dateService.isDebugMode()">
      <mat-card-header>
        <!-- <mat-icon mat-card-avatar>bug_report</mat-icon> -->
        <!-- <mat-card-title>Debug Dátum Beállítás</mat-card-title> -->
        <mat-card-subtitle>
          @if (dateService.isDebugMode()) {
            Aktív: {{ dateService.formatDebugDate(dateService.getCurrentDate()) }}
          } @else {
            Jelenleg a valós dátumot használja
          }
        </mat-card-subtitle>
      </mat-card-header>

      <mat-card-content>
        <div class="debug-controls">
          <mat-slide-toggle 
            [checked]="dateService.isDebugMode()"
            (change)="onDebugModeToggle($event.checked)"
            color="primary">
            Debug mód aktiválása
          </mat-slide-toggle>

          @if (dateService.isDebugMode()) {
            <div class="date-picker-container">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Teszt dátum</mat-label>
                <input 
                  matInput 
                  [matDatepicker]="picker"
                  [value]="selectedDate"
                  (dateChange)="onDateChange($event.value)"
                  placeholder="Válassz dátumot">
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>

            </div>

            <div class="debug-info">
              <h4>Debug információk:</h4>
              <div class="info-row">
                <span class="label">Aktuális dátum:</span>
                <span class="value">{{ dateService.formatDebugDate(dateService.getCurrentDate()) }}</span>
              </div>
              <div class="info-row">
                <span class="label">Hét szám:</span>
                <span class="value">{{ getCurrentWeekInfo().weekNumber }}. hét, {{ getCurrentWeekInfo().year }}</span>
              </div>
              <div class="info-row">
                <span class="label">Hónap:</span>
                <span class="value">{{ getCurrentMonthInfo().year }}. {{ getCurrentMonthInfo().month.toString().padStart(2, '0') }}.</span>
              </div>
            </div>
          }
        </div>
      </mat-card-content>

      <mat-card-actions>
        @if (dateService.isDebugMode()) {
          <button mat-raised-button color="warn" (click)="clearDebugDate()">
            <mat-icon>clear</mat-icon>
            Debug mód kikapcsolása
          </button>
        }
      </mat-card-actions>
    </mat-card>
  `,
  styles: [`
    .debug-card {
      margin: 16px 0;
      border: 2px solid #e0e0e0;
      transition: all 0.3s ease;
    }

    .debug-card.debug-active {
      border-color: #ff9800;
      background-color: #fff3e0;
    }

    .debug-controls {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .date-picker-container {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      flex-wrap: wrap;
    }

    .full-width {
      flex: 1;
      min-width: 200px;
    }

    .time-input {
      width: 80px;
    }

    .quick-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .quick-actions button {
      min-width: 120px;
    }

    .debug-info {
      background: #f5f5f5;
      padding: 8px;
      border-radius: 4px;
      margin-top: 8px;
      font-size: 12px;
    }

    .debug-info h4 {
      margin: 0 0 8px 0;
      color: #666;
      font-size: 14px;
    }

    .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 4px;
    }

    .label {
      font-weight: 500;
      color: #555;
    }

    .value {
      color: #333;
      font-family: 'Courier New', monospace;
    }

    @media (max-width: 768px) {
      .date-picker-container {
        flex-direction: column;
      }
      
      .full-width,
      .time-input {
        width: 100%;
      }
      
      .quick-actions {
        justify-content: center;
      }
      
      .quick-actions button {
        flex: 1;
        min-width: auto;
      }
    }
  `]
})
export class DebugDateControlComponent {
  dateService = inject(DateService);
  
  selectedDate: Date | null = null;
  selectedHour: number = 0;
  selectedMinute: number = 0;

  constructor() {
    // Initialize with current debug date if set
    const debugDate = this.dateService.debugDate();
    if (debugDate) {
      this.selectedDate = new Date(debugDate);
      this.selectedHour = debugDate.getHours();
      this.selectedMinute = debugDate.getMinutes();
    }
  }

  onDebugModeToggle(enabled: boolean): void {
    if (enabled) {
      const now = new Date();
      this.selectedDate = now;
      this.selectedHour = now.getHours();
      this.selectedMinute = now.getMinutes();
      this.updateDebugDate();
    } else {
      this.dateService.clearDebugDate();
    }
  }

  onDateChange(date: Date | null): void {
    if (date) {
      this.selectedDate = date;
      this.updateDebugDate();
    }
  }

  onTimeChange(event: any): void {
    const target = event.target as HTMLInputElement;
    const value = parseInt(target.value) || 0;
    
    if (target.placeholder === '00' && target.previousElementSibling?.textContent?.includes('Óra')) {
      this.selectedHour = Math.max(0, Math.min(23, value));
    } else {
      this.selectedMinute = Math.max(0, Math.min(59, value));
    }
    
    this.updateDebugDate();
  }

  setToToday(): void {
    const now = new Date();
    this.selectedDate = now;
    this.selectedHour = now.getHours();
    this.selectedMinute = now.getMinutes();
    this.updateDebugDate();
  }

  setToYesterday(): void {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.selectedDate = yesterday;
    this.selectedHour = yesterday.getHours();
    this.selectedMinute = yesterday.getMinutes();
    this.updateDebugDate();
  }

  setToNextWeek(): void {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    this.selectedDate = nextWeek;
    this.selectedHour = nextWeek.getHours();
    this.selectedMinute = nextWeek.getMinutes();
    this.updateDebugDate();
  }

  setToLastWeek(): void {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    this.selectedDate = lastWeek;
    this.selectedHour = lastWeek.getHours();
    this.selectedMinute = lastWeek.getMinutes();
    this.updateDebugDate();
  }

  clearDebugDate(): void {
    this.dateService.clearDebugDate();
    this.selectedDate = null;
    this.selectedHour = 0;
    this.selectedMinute = 0;
  }

  getCurrentWeekInfo() {
    return this.dateService.getCurrentWeekInfo();
  }

  getCurrentMonthInfo() {
    return this.dateService.getCurrentMonthInfo();
  }

  private updateDebugDate(): void {
    if (this.selectedDate) {
      const debugDate = new Date(this.selectedDate);
      debugDate.setHours(this.selectedHour, this.selectedMinute, 0, 0);
      this.dateService.setDebugDate(debugDate);
    }
  }
}